import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Hotel, Search, Phone, Mail, Globe, ExternalLink } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const hotelIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#00599c;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

export function AccommodationsPage() {
  const { language } = useLanguage();
  
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const t = {
    title: language === 'es' ? 'Alojamientos' : 'Accommodations',
    subtitle: language === 'es' ? 'Hoteles, apartamentos y viviendas vacacionales' : 'Hotels, apartments and vacation rentals',
    searchPlaceholder: language === 'es' ? 'Buscar por nombre o municipio...' : 'Search by name or municipality...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    allTypes: language === 'es' ? 'Todos' : 'All',
    filterByType: language === 'es' ? 'Filtrar por tipo' : 'Filter by type',
    loading: language === 'es' ? 'Cargando alojamientos...' : 'Loading accommodations...',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
  };

  // Accommodation type colors
  const typeColors = {
    'Hotel': '#00599c',
    'Apartamento/Extrahotelero': '#8b5cf6',
    'Vivienda Vacacional': '#f59e0b',
  };

  // Fetch accommodations
  const fetchAccommodations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tourism/accommodations`);
      setAccommodations(response.data || []);
    } catch (error) {
      console.error('Error fetching accommodations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccommodations();
  }, [fetchAccommodations]);

  // Get unique types for filter
  const types = useMemo(() => {
    const typesSet = new Set();
    accommodations.forEach(a => {
      if (a.categoria) typesSet.add(a.categoria);
    });
    return Array.from(typesSet).sort();
  }, [accommodations]);

  // Get coordinates for an item
  const getCoords = (item) => {
    if (item.lat && item.lon) {
      return [item.lat, item.lon];
    }
    return null;
  };

  // Filter data based on search and type
  const filteredAccommodations = useMemo(() => {
    let filtered = accommodations;
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.categoria === typeFilter);
    }
    
    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.nombre || '').toLowerCase().includes(searchLower) ||
        (item.municipio || '').toLowerCase().includes(searchLower) ||
        (item.direccion || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [accommodations, search, typeFilter]);

  // Map markers with coordinates
  const mapMarkers = useMemo(() => {
    return filteredAccommodations
      .map((item, idx) => ({ ...item, coords: getCoords(item), idx }))
      .filter(item => item.coords);
  }, [filteredAccommodations]);

  const centerPosition = [28.2916, -16.5291];

  const openInGoogleMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Hotel className="w-5 h-5 text-[#00599c]" />
          <span className="text-sm font-medium text-[#00599c] uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="accommodations-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4">
        <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
          <span className="text-2xl font-bold text-[#00599c]">{accommodations.length}</span>
          <span className="text-gray-600 ml-2">
            {language === 'es' ? 'alojamientos' : 'accommodations'}
          </span>
        </div>
      </div>

      {/* Type Filters */}
      {!loading && (
        <div className="mb-4">
          <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                typeFilter === 'all'
                  ? 'bg-[#00599c] text-white'
                  : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#00599c]'
              }`}
            >
              {t.allTypes} ({accommodations.length})
            </button>
            {types.map((tipo) => {
              const count = accommodations.filter(a => a.categoria === tipo).length;
              const color = typeColors[tipo] || '#6b7280';
              const isActive = typeFilter === tipo;
              return (
                <button
                  key={tipo}
                  onClick={() => setTypeFilter(tipo)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? 'text-white shadow-md'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                  }`}
                  style={isActive ? { backgroundColor: color } : {}}
                >
                  {tipo} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      {!loading && (
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white border-slate-200"
              data-testid="accommodations-search"
            />
          </div>
          <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
            {t.showing} <strong className="text-[#00599c]">{filteredAccommodations.length.toLocaleString()}</strong> {t.of} {accommodations.length.toLocaleString()} {t.results}
          </div>
        </div>
      )}

      {/* Map */}
      <Card className="overflow-hidden border-slate-200 mb-6" data-testid="accommodations-map">
        <div className="h-[400px] md:h-[450px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50">
              <Spinner size="lg" />
              <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
            </div>
          ) : (
            <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapLocationButton language={language} />
              <MarkerClusterGroup 
                chunkedLoading 
                iconCreateFunction={createClusterIcon} 
                maxClusterRadius={50}
              >
                {mapMarkers.map((item) => (
                  <Marker 
                    key={item.idx} 
                    position={item.coords} 
                    icon={hotelIcon}
                  >
                    <Popup>
                      <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                        <h3 className="font-bold text-[#252a34] mb-1">{item.nombre}</h3>
                        
                        {item.categoria && (
                          <Badge 
                            className="text-xs mb-2 text-white"
                            style={{ backgroundColor: typeColors[item.categoria] || '#6b7280' }}
                          >
                            {item.categoria}
                          </Badge>
                        )}
                        
                        {item.direccion && (
                          <p className="text-sm text-[#252a34]/70 mb-1">📍 {item.direccion}</p>
                        )}
                        
                        {item.municipio && (
                          <p className="text-xs text-[#252a34]/50 mb-2">{item.municipio}</p>
                        )}
                        
                        {item.telefono && (
                          <p className="text-xs text-[#252a34]/60 flex items-center gap-1 mb-1">
                            <Phone className="w-3 h-3" /> {item.telefono}
                          </p>
                        )}
                        
                        {item.email && (
                          <p className="text-xs text-[#252a34]/60 flex items-center gap-1 mb-1">
                            <Mail className="w-3 h-3" /> {item.email}
                          </p>
                        )}
                        
                        {item.web && (
                          <a 
                            href={item.web.startsWith('http') ? item.web : `https://${item.web}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-2"
                          >
                            <Globe className="w-3 h-3" /> Web
                          </a>
                        )}
                        
                        <button 
                          onClick={() => openInGoogleMaps(item.lat, item.lon)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {t.openInGoogleMaps}
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </Card>

      {/* List */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAccommodations.slice(0, 30).map((item, idx) => (
            <Card key={idx} className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#252a34] mb-2">{item.nombre}</h3>
                {item.categoria && (
                  <Badge 
                    className="text-xs mb-2 text-white"
                    style={{ backgroundColor: typeColors[item.categoria] || '#6b7280' }}
                  >
                    {item.categoria}
                  </Badge>
                )}
                {item.direccion && (
                  <p className="text-sm text-[#252a34]/70 mb-1">📍 {item.direccion}</p>
                )}
                {item.municipio && (
                  <p className="text-xs text-[#252a34]/50 mb-2">{item.municipio}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  {item.telefono && (
                    <span className="text-xs text-[#252a34]/60 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {item.telefono}
                    </span>
                  )}
                </div>
                {item.lat && item.lon && (
                  <button 
                    onClick={() => openInGoogleMaps(item.lat, item.lon)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {t.openInGoogleMaps}
                  </button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
